import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import LocationMarkerFilled from '@uber/base-icons/location-marker-filled';
import { styled, useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import { currentFrameAtom } from '../../../../../../labelling-v2/atoms/video-playback-atoms';
import { useMediaStateIsPlaying } from '../../../../../providers/hooks/use-media-state';
import { CURRENT_FRAME_MARKER_Z_INDEX, DRAG_HANDLE_SIZE, FRAME_WIDTH, MARKER_LINE_SIZE } from '../constants';
import type { CurrentFrameMarkerRefT, OnFrameChangeT } from '../types';
import { useAnimateOnCurrentFrameChange } from './hooks/useAnimateOnCurrentFrameChange';
import { useDragHandleOnMouseDown } from './hooks/useDragHandleOnMouseDown';
import { useSetPositionOnInitialRender } from './hooks/useSetPositionOnInitialRender';
type PropsT = {
  onFrameChange: OnFrameChangeT;
  maxFrames: number;
  gridContainer: HTMLDivElement | null;
  isDisabled: boolean;
};
const Container = styled('div', () => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 0,
  height: '100%',
  pointerEvents: 'none',
  zIndex: CURRENT_FRAME_MARKER_Z_INDEX,
  willChange: 'transform'
}));
const MarkerHandle = styled<'div', {
  $isDisabled: boolean;
}>('div', ({
  $isDisabled
}) => ({
  position: 'absolute',
  top: `${-DRAG_HANDLE_SIZE}px`,
  left: `${-DRAG_HANDLE_SIZE / 2}px`,
  width: `${DRAG_HANDLE_SIZE}px`,
  height: `${DRAG_HANDLE_SIZE}px`,
  pointerEvents: 'auto',
  cursor: $isDisabled ? 'not-allowed' : 'grab',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));
const MarkerLine = styled('div', ({
  $theme
}) => ({
  position: 'absolute',
  top: `${-DRAG_HANDLE_SIZE / 4}px`,
  left: `${-MARKER_LINE_SIZE / 2}px`,
  width: `${MARKER_LINE_SIZE}px`,
  bottom: 0,
  pointerEvents: 'none',
  backgroundColor: $theme.colors.brandContentPrimary
}));
export const _CurrentFrameMarker = forwardRef<CurrentFrameMarkerRefT, PropsT>(({
  onFrameChange,
  maxFrames,
  gridContainer,
  isDisabled
}, ref) => {
  const [, theme] = useStyletron();
  const containerRef = useRef<HTMLDivElement>(null);
  const markerHandleRef = useRef<HTMLDivElement>(null);
  const onFrameChangeRef = useRef(onFrameChange);
  // eslint-disable-next-line react-hooks/refs
  onFrameChangeRef.current = onFrameChange;
  const currentFrame = useAtomValue(currentFrameAtom);
  const isPlaying = useMediaStateIsPlaying();
  const lastFrameRef = useRef(currentFrame);
  const applyFramePosition = useCallback((scrollLeft: number) => {
    const x = lastFrameRef.current * FRAME_WIDTH + FRAME_WIDTH / 2 - scrollLeft;
    containerRef.current && (containerRef.current.style.transform = `translateX(${x}px)`);
  }, []);
  const isDraggingDisabled = isPlaying || isDisabled;
  useAnimateOnCurrentFrameChange({
    frame: currentFrame,
    lastFrameRef,
    applyFramePosition,
    gridContainer
  });
  useSetPositionOnInitialRender(applyFramePosition);
  useDragHandleOnMouseDown({
    isDraggingDisabled,
    applyFramePosition,
    lastFrameRef,
    markerHandleRef,
    onFrameChangeRef,
    gridContainer,
    containerRef,
    maxFrames
  });
  useImperativeHandle(ref, () => ({
    sync: applyFramePosition
  }), [applyFramePosition]);
  return <Container ref={containerRef} data-testid="current-frame-marker">
        <MarkerHandle ref={markerHandleRef} data-testid="current-frame-marker-handle" $isDisabled={isDraggingDisabled}>
          <LocationMarkerFilled size={16} color={theme.colors.brandContentPrimary} />
        </MarkerHandle>

        <MarkerLine data-testid="current-frame-marker-line" />
      </Container>;
});
_CurrentFrameMarker.displayName = 'CurrentFrameMarker';
export const CurrentFrameMarker = React.memo(_CurrentFrameMarker);